<template>
    <div class="container-fluid px-4">

        <BaseTable :rows="workgroups" :columns="columns" :loading="loading" :searchable="false" :selectable="false"
            :pagination="false" @searchChanged="onSearchChanged">

            <template #col-aiwgSummary="{ row }">
                <button class="btn btn-sm btn-outline-success my-0" @click="openAIWGSummary(row)">
                    <i class="material-icons text-sm align-middle">summarize</i>
                </button>
            </template>

            <template #actions="{ row }">
                <button v-if="canEditIWG(row.id)" class="btn btn-sm my-0 py-0" @click="editInstances(row.id)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button v-else class="btn btn-sm my-0 py-0" @click="editInstances(row.id)">
                    <i class="material-icons text-lg text-info">visibility</i>
                </button>
                <button class="btn btn-sm my-0 py-0" @click="openLogsModal(row)">
                    <i class="material-icons text-lg text-info">chat</i>
                </button>
                <button v-if="canEditIWG(row.id)" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-danger">delete</i>
                </button>
            </template>
        </BaseTable>

        <ContentModal v-model="showLogs" title="Audit Logs" size="xl">
            <BaseTable :columns="logColumns" :rows="auditLogs" :searchable="false" :pagination="false" class="mt-3" />
        </ContentModal>

        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedWorkgroup?.workgroup_name || 'this workgroup'" 
            @confirm="deleteWorkgroup" />

        <SummaryModal v-model="showAIWGSummaryModal" :title="aiwgSummaryTitle" :summaryList="aiwgSummaryData" />
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { useRoute, useRouter } from 'vue-router'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '../../../components/Modal/ContentModal.vue'
import DeleteModal from '@/components/Modal/ConfirmationModal.vue'
import { usePermissions } from '@/composables/usePermissions'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import SummaryModal from '@/components/Modal/SummaryModal.vue'

defineProps({
    readOnly: { type: Boolean, default: false }
})

const { canEditInstanceWorkgroup } = usePermissions()
const canEditIWG = (iwgId) => canEditInstanceWorkgroup(iwgId)
const { pushCrumb } = useBreadcrumb()

const toast = useToast()
const store = useStore()
const route = useRoute()
const router = useRouter()

const searchTerm = ref('')
const selectedWorkgroup = ref(null)
const showLogs = ref(false)
const showDeleteModal = ref(false)
const auditLogs = ref([])

const locationId = route.params.locationid
const activityInstanceId = route.params.activityinstanceid

const workgroups = computed(() => {
    const allWorkgroups = store.getters['instanceWorkgroups'] || []
    if (!searchTerm.value) return allWorkgroups
    
    const term = searchTerm.value.toLowerCase()
    return allWorkgroups.filter(wg => 
        wg.name?.toLowerCase().includes(term) ||
        wg.description?.toLowerCase().includes(term) ||
        wg.skillname?.toLowerCase().includes(term) ||
        wg.teamname?.toLowerCase().includes(term)
    )
})
const loading = computed(() => store.getters.instanceLoading)

const columns = [
    { key: 'workgroup_name', label: 'Workgroup Name' },
    { key: 'aiwgSummary', label: 'Summary' },
    { key: 'description', label: 'Description' },
    { key: 'person_count', label: 'Person Count' },
    { key: 'persons_scheduled', label: 'Persons Scheduled' },
    { key: 'priority', label: 'Priority' },
    { key: 'skill', label: 'Skill' },
    { key: 'team', label: 'Team' },
]

const logColumns = [
    { key: "messagedt", label: "Date/Time" },
    { key: "message", label: "Message" },
]

const showAIWGSummaryModal = ref(false)
const aiwgSummaryData = ref([])
const aiwgSummaryTitle = ref('')

const openAIWGSummary = async (row) => {
    aiwgSummaryTitle.value = `Summary: ${row.workgroup_name}`
    try {
        aiwgSummaryData.value = await store.dispatch('fetchAIWGSummary', row.id)
        showAIWGSummaryModal.value = true
    } catch (e) {
        console.error('Error loading AIWG summary:', e)
    }
}

// Fetch workgroups for the instance
const fetchInstanceWorkgroups = async () => {
    try {
        await store.dispatch('getInstanceWorkgroups', activityInstanceId)
    } catch (error) {
        console.error('Error fetching instance workgroups:', error)
        toast.error('Failed to load instance workgroups', { position: 'top-right' })
    }
}

const onSearchChanged = (term) => {
    searchTerm.value = term
}

const openLogsModal = async (row) => {
    selectedWorkgroup.value = row
    showLogs.value = true
    try {
        const logs = await store.dispatch('getWorkgroupAuditLogs', row.id)
        auditLogs.value = logs || []
    } catch (error) {
        console.error('Error fetching audit logs:', error)
        toast.error('Failed to load audit logs', { position: 'top-right' })
        auditLogs.value = []
    }
}

const openDeleteModal = (row) => {
    selectedWorkgroup.value = row
    showDeleteModal.value = true
}

const deleteWorkgroup = async () => {
    if (!selectedWorkgroup.value) return

    try {
        const workgroupId = selectedWorkgroup.value.id
        await store.dispatch('deleteWorkgroupInstance', {
            locationId,
            workgroupId
        })
        
        toast.success('Workgroup deleted successfully', { position: 'top-right' })
        
        // Reload workgroups after deletion
        await fetchInstanceWorkgroups()
    } catch (error) {
        console.error('Error deleting workgroup:', error)
        toast.error('Failed to delete workgroup', { position: 'top-right' })
    } finally {
        showDeleteModal.value = false
        selectedWorkgroup.value = null
    }
}

const editInstances = (id) => {
    const row = workgroups.value.find(r => r.id === id)
    pushCrumb({
        label: row?.workgroup_name || `Instance Workgroup #${id}`,
        icon: 'groups',
        route: { name: 'editWorkgroupInstance', params: { locationid: locationId, instanceworkgroupid: id } },
    })
    router.push({
        name: 'editWorkgroupInstance',
        params: { locationid: locationId, instanceworkgroupid: id },
    })
}

onMounted(() => {
    fetchInstanceWorkgroups()
})
</script>